<template>
<div>
  <b-modal :active.sync="showPostsConfigurator" v-if="showPostsConfigurator" has-modal-card>
    <posts-configurator :projid="projid" :volid="volid" />
  </b-modal>
  
  <b-modal :active.sync="showCoverConfigurator" has-modal-card>
    <cover-configurator :projid="projid" :volid="volid" @change="updateCover"/>
  </b-modal>
  
  <b-modal :active.sync="showExpert" has-modal-card>
    <expert-mode :projid="projid"/>
  </b-modal>

  <b-modal :active.sync="showVoluminator" has-modal-card>
    <voluminator-page :projid="projid" @change="updateCover"/>
  </b-modal>

  <b-modal v-if="devMode" :active.sync="showPostEditor" has-modal-card>
    <post-editor-page :projid="projid" :pid="editPid"/>
  </b-modal>

  <div id="maincontent" class="section" v-if="showUi">
    <section id="previewbox">
      <div class="keymapped" tabindex="0"
           @keyup.n="previewPostNext"
           @keyup.p="previewPostPrev"
           @keyup.arrow-right="previewNext"
           @keyup.arrow-left="previewPrev">
        <div id="preview-area" class="box">
          <div v-if="projectLoaded && (!project.isLocked || devMode)">
            <router-link v-if="isVolumated && !showVolumesList"
                         class="button"
                         id="back-to-volumes-list"
                         :to="'/projects/'+projid">
              <b-tooltip label="Back to Volumes List">
                <span>&lt;&lt;</span>
                <b-icon icon="book-open-variant"/>
              </b-tooltip>
            </router-link>

            <a :class="buttonClass"
               @click="showPostsConfigurator = !showPostsConfigurator">
              <b-icon icon="book-open"/>
              <span>Pick Posts</span>
              <b-icon icon="menu-down"/>
            </a>

            <a v-if="!showVolumesList"
               :class="buttonClass"
               @click="showCoverConfigurator = !showCoverConfigurator">
              <b-icon icon="image-album"/>
              <span>Customise Covers</span>
              <b-icon icon="menu-down"/>
            </a>

            <a v-if="!showVolumesList && volumesRequired > 1" :class="buttonClass"
               @click="showVoluminator = !showVoluminator">
              <b-icon icon="arrow-split-vertical"/>
              <span>Split Volumes</span>
              <b-icon icon="menu-down"/>
            </a>

            <b-tooltip label="Tweaks">
              <a :class="buttonClass"
                 @click="showExpert = !showExpert">
                <b-icon icon="auto-fix"/>
                <b-icon icon="menu-down"/>
              </a>
            </b-tooltip>

            <a class="button is-primary" v-if="canPrint"
               @click="addToCart">
              <b-icon icon="cart"/>
              <span>Add to Cart</span>
            </a>
          </div>

          <div id="preview-holder-outer" :class="'format-'+format">
            <div id="preview-holder">
              <div v-if="showVolumesList" class="volumes-list preview-box">
                  <gallery-card v-for="vol in volumesInNumericalOrder"
                                class="volume-card"
                                :key="vol.volumeNumber"
                                :link="projid+'/vol'+vol.volumeNumber"
                                :title="'Volume '+vol.volumeNumber"
                                :image="(previews['cover-vol'+vol.volumeNumber]||{}).p0"/>
              </div>
              <div v-else class="preview-box">
                <flip-book :format="format" :pages="previewPages"
                           :pagenum.sync="previewPageNumber">
                  <template slot-scope="page">
                    <div class="page">
                      <div class="blank" v-if="page.content.pid=='_blank'"></div>
                      <div v-else class="contents fit-parent" :class="{pending: page.content.pending}">
                        <img v-if="page.content.img"
                             :src="page.content.img"
                             :style="'transform: translateY(-'+(page.content.offset*100.0)+'%);'"/>
                        <div v-else class="loading fit-parent">Loading</div>
                      </div>
                    </div>
                  </template>
                </flip-book>
              </div>
            </div>
            
            <transition name="fade">
              <div class="hint clickable" v-if="showLookInsideHint"
                   id="look-inside"  @click="previewNext()">
                <center><b-icon icon="chevron-right" type="is-primary" size="is-large"></b-icon>
                  <br>look inside!</center>
              </div>
            </transition>
            
          </div>

          
          <b-message class="errormsg is-danger" v-if="errormsg"
                     title="Something's gone wrong!">
            <p v-html="errormsg"/>
          </b-message>
          
            <div class="hbox" v-if="!showVolumesList">
              <div v-if="devMode" class="buttonbox dev-only">
                <a class="button dev-only" @click="forceLayoutPost(pageLeftPid)"><b-icon icon="redo-variant"/></a>
                <a class="button dev-only" @click="forceRenderPost(pageLeftPid)"><b-icon icon="image"/></a>
                <a class="button dev-only" @click="editPid=pageLeftPid; showPostEditor=true"><b-icon icon="pencil"/></a>
              </div>
              <div class="buttonbox">
                <a class="button is-outlined" title="(p) Previous Post" @click="previewPostPrev()">&lt;&lt;</a>
                <a class="button is-outlined" title="(left) Previous Page" @click="previewPrev()">&lt;</a>
                <a class="button is-outlined" title="(right) Next Page" @click="previewNext()">&gt;</a>
                <a class="button is-outlined" title="(n) Next Post" @click="previewPostNext()">&gt;&gt;</a>
              </div>
              <div class="slider">
                <b-slider v-model="previewPageNumber" :tooltip="false"
                          :min="0" :max="Math.max(previewPages.length, 0)" :step="2"/>
              </div>
              <div v-if="devMode" class="buttonbox dev-only">
                <a class="button dev-only" @click="forceLayoutPost(pageRightPid)"><b-icon icon="redo-variant"/></a>
                <a class="button dev-only" @click="forceRenderPost(pageRightPid)"><b-icon icon="image"/></a>
                <a class="button dev-only" @click="editPid=pageRightPid; showPostEditor=true"><b-icon icon="pencil"/></a>
              </div>
              <b-select class="bookmarks" v-model="bookmarkCurrent">
                <option v-for="p in bookmarks" :value="p.pid">
                  <i>{{p.date | formatDate}} - </i>
                  <span v-if="p.title && !p.title.startsWith('&nbsp;')">{{p.title}}</span>
                  <span v-if="devMode"> {{p.pid}}</span></option>
              </b-select>
            </div>

        </div>
      </div>
    </section>

    <section class="box tile is-ancestor" id="infobox">
        <div class="tile is-child">
          <h3 class="title">{{ project.title || project.site }}</h3>
          <div v-if="showVolumesList">
            <span class="subtitle"> A Book in {{numVolumes}} Volumes</span>
          </div>

          <span v-else-if="numVolumes > 1" class="subtitle">
            Volume {{currentVolume.volumeNumber}} of {{numVolumes}}</span>
          <br>
          <ul>
            <li>A4 photo quality hardback.
            <li v-if="!canPrint"><span class="approx">Approximately:</span></li>
            <li>{{totalPages}} pages<span v-if="volumesRequired<=1">,</span>
              <span v-else> in {{volumesRequired}} volumes<br></span>
              {{$store.state.currencySymbol}}{{expectedPrice| formatCurrency}}
              <span v-if="volumesRequired>1">(~{{$store.state.currencySymbol}}{{expectedPrice/volumesRequired| formatCurrency}}
                per volume)</span>.</li>
          </ul>
        </div>

        <b-message v-if="!showVolumesList && tooManyPages && rendersDone.length == selectedPosts.length"
                   class="tile is-child is-warning"
                   :closable="false">
          <b-tag size="is-large" type="is-warning" class="warning-banner"><h3 class="subtitle">
              <b-icon icon="lightbulb-on-outline"/>&nbsp;&nbsp;This book
              requires splitting!</h3></b-tag>
          <p>This book is too large to print. It needs to be split into
              multiple volumes. Once you are happy with the content,
              click <a class="button inline-button is-primary"
              @click="showVoluminator = !showVoluminator">
              <b-icon icon="arrow-split-vertical"/>
              <span>Split Volumes</span>
              <b-icon icon="menu-down"/>
            </a> to break this book into mutiple volumes
            automatically. This will create {{volumesRequired}} volumes
            each of approximately
            {{parseInt(totalPages/volumesRequired)}} pages.</p><br>
          <p><i>If you wish to fit it into a single volume, reduce the
            amount of content by
            using <a @click="showPostsConfigurator=!showPostsConfigurator">Pick
            Posts</a> to narrow the post selection. Alternatively, you
            create multiple volumes manually using other criteria
            by <router-link to="/doc/faq#volumes">following this
            guide</router-link>. </i></p>
          <b-tag class="is-warning">Maximum {{maxPages}} pages per volume!</b-tag>
        </b-message>

        <b-message v-if="canPrint" class="tile is-child
                                               is-success">
          <b-tag size="is-large" type="is-success" class="warning-banner"><h3 class="subtitle">
              <b-icon icon="emoticon-happy"/>&nbsp;&nbsp;It's Ready!</h3></b-tag>

          <a class="button is-primary is-large" @click="addToCart">
            <b-icon icon="cart"/>
            <span v-if="showVolumesList">Add All to Cart</span>
            <span v-else="showVolumesList">Add to Cart</span>
          </a>
          <span v-if="showVolumesList"><br>Please check and add each volume to your cart individually.</span>
          <a class="button is-warning is-large" v-if="devMode"
             :disabled="!canPrint" @click="publish">
            <b-icon icon="share"/>
            <span>Share</span>
          </a>
          <a v-if="published" :href="'/gallery/'+published">Public Gallery</a>
        </b-message>

        <b-message v-if="showVolumesList && !project.isLocked" class="tile is-child"
                   :closable="false">
          <p><a class="button inline-button is-primary" @click="showVoluminator = !showVoluminator">
              <b-icon icon="arrow-split-vertical"/>
              <span>Undo Volumes</span>
              <b-icon icon="menu-down"/>
            </a> if you have changed the content.</p>
        </b-message>

        <b-message v-if="!canPrint && rendersDone.length < selectedPosts.length" class="tile is-child">
          <b-tag size="is-large" class="warning-banner"><h3 class="subtitle">
              <b-icon icon="coffee"/>&nbsp;&nbsp;Working on it!</h3></b-tag>
          <p>
            Still working,
            <progress class="progress" :value="progressPercent" :max="100"/>
            {{progressPercent}}%
            done...</p>
        </b-message>
    </section>

    <div class="container box" v-if="devMode">

      <b-collapse class="card" :open="false">
        <div slot="trigger" slot-scope="props" class="card-header">          
          <p class="card-header-title">{{renderTagsString}}:<br>
            Layouted: {{layoutsDone.length}} of {{selectedPosts.length}}<br>
            Rendered: {{rendersDone.length}} of {{selectedPosts.length}}<br>
            Page: {{previewPageNumber}} of {{previewPages.length}}
            <br>Success:{{taskSuccessCount}} Errors:{{taskErrorCount}}
          </p>
          
          <a class="card-header-icon"><b-icon :icon="props.open?'menu-up':'menu-down'"></b-icon></a>
        </div>
        <b-table :data="selectedPosts" :mobile-cards="false" style="margin: auto;">
          <b-table-column label="Post" field="pid" sortable v-slot="props">
            {{props.row.pid}}
          </b-table-column>
          <b-table-column label="Title" field="title" sortable v-slot="props">
            {{props.row.date | formatDate}} {{props.row.title}}<a target="_blank" :href="props.row.link"><b-icon icon="link"/></a>
          </b-table-column>
          <b-table-column label="Actions" v-slot="props">
            <button @click="forceLayoutPost(props.row.pid)"><b-icon icon="redo-variant"/>L</button>
            <button @click="forceRenderPost(props.row.pid)"><b-icon icon="image"/>R</button>
            <button @click="editPid=props.row.pid; showPostEditor=true"><b-icon icon="pencil"/>E</button>
            <button @click="bookmarkCurrent = props.row.pid"><b-icon icon="bookmark-outline"/>Go</button>
          </b-table-column>
          <b-table-column label="Layout" v-slot="props">
            {{pageEstimates[props.row.pid]===undefined ? '-' : pageEstimates[props.row.pid]}}
          </b-table-column>
          <b-table-column label="Rendered" v-slot="props">
            {{pageCounts[props.row.pid]===undefined ? '-' : pageCounts[props.row.pid]}}
          </b-table-column>
        </b-table>
      </b-collapse>

      <b-collapse class="card" :open="false" v-if="taskErrorCount">
        <div slot="trigger" slot-scope="props" class="card-header">          
          <p class="card-header-title">Errors:{{taskErrorCount}} </p>
          <a class="card-header-icon"><b-icon :icon="props.open?'menu-up':'menu-down'"></b-icon></a>
        </div>
        <div v-for="err in taskErrors" style="margin: auto;">
          <b v-html="err.tasker"/>
          <a v-for="pid in err.pids" v-html="pid" @click="bookmarkCurrent = props.row.pid"/>
          <b v-html="'x' + err.count"/>
          <p>{{err.info.message}} : ({{err.info.detail}})</p>
          <p v-if="err.info.stack" v-html="err.info.stack.replaceAll('\n','<br>').replaceAll(' ','&nbsp;')"/>
        </div>
      </b-collapse>
    </div>
  </div>
</div>
</template>

<script>
import Firebase from '../firebase.js'
import Tasker from '../tasker.js'
import Pricer from '../pricer.js'
import ActionLog from '../actionlog.js'
import Defaults from '../defaults.js'
import FlipBook from '@/components/FlipBook'
import GalleryCard from '@/components/GalleryCard'
import CoverConfigurator from '@/components/CoverConfigurator'
import PostsConfigurator from '@/components/PostsConfigurator'
import ExpertMode from '@/components/ExpertMode'
import VoluminatorPage from '@/components/VoluminatorPage'
import CurrencySelector from '@/components/CurrencySelector'
import PostEditorPage from '@/components/PostEditorPage'
import { hashString } from '../util.js'
var db = Firebase.database


export default {
  name: 'ProjPage',  
  components: {
    FlipBook,
    GalleryCard,
    CoverConfigurator,
    PostsConfigurator,
    ExpertMode,
    VoluminatorPage,
    CurrencySelector,
    PostEditorPage,
  },
  metaInfo() { return {
    title: 'Project: '+(this.project ? this.project.title : '??'),
  }},
  data: function() { return {
    projid: undefined,
    volid: undefined,

    // to $bind later
    project: undefined,
    posts: undefined,
    pageEstimates: undefined,
    pageCounts: undefined,
    previews: undefined,
    
    renderStatsAvailability: 0,
    
    previewPageNumber: 0,
    previewTasks: {},

    initialLoad: true,
    layouter: undefined,
    renderer: undefined,
    layoutRequests: [],
    renderRequests: [],
    relayoutRequests: [],
    rerenderRequests: [],
    taskSuccessCount: 0,
    taskErrorCount: 0,
    taskErrors: [],

    showCoverConfigurator: false,
    showPostsConfigurator: false,
    showExpert: false,
    showVoluminator: false,
    showPostEditor: false,
    editPid: undefined,

    hasViewedInside: false,
    idleTimeout: undefined,
    published: undefined,
  }},
  computed: {
    uid: function() { return this.$store.state.uid },
    bookid: function() {
      if (this.volid) this.projid+'/'+this.volid
      return this.projid
    },
    format: function() {
      return this.projectLoaded ? this.project.selections.format || 'a4' : 'xx'
    },
    isVolumated: function() {
      return this.projectLoaded && this.project.selections.volumes
    },
    numVolumes: function() {
      if (this.projectLoaded && this.project.selections.volumes) {
        return Object.values(this.project.selections.volumes).length
      }
      return 1
    },
    volumesInNumericalOrder: function() {
      if (this.projectLoaded && this.project.selections.volumes) {
        return Object.values(this.project.selections.volumes).sort((a,b)=>a.volumeNumber-b.volumeNumber)
      }
      return []
    },
    showVolumesList: function() {
      return this.numVolumes > 1 && !this.volid
    },
    currentVolume: function() {
      if (this.projectLoaded) {
        var volumes = this.project.selections.volumes
        if (volumes && volumes[this.volid]) {
          return volumes[this.volid]
        }
        return this.project.selections
      }
      return {}
    },
    selectedPostsIds: function() {
      var pids = this.currentVolume.selectedPosts || []
      if (this.hasTableOfContents) {
        pids.unshift(this.tocName)
      }
      return pids
    },
    projectLoaded: function() {
      return this.project && this.project.selections
    },
    showUi: function() {
      return this.projectLoaded && this.renderStatsReady
    },
    selectedPosts: function() {
      var posts = this.posts;
      if (this.selectedPostsIds) {
        var sels =  this.selectedPostsIds
            .map(p => (''+p).startsWith('toc')
                 ? { title: 'ToC', pid: p }
                 : posts[p])
            .filter(p => p)
        //console.log("selectedPosts: ", this.selectedPostsIds, sels, posts)
        return sels
      }
      return []
    },
    bookmarks: function() {
      var marks = this.selectedPosts.slice()
      marks.unshift({pid: this.coverName, title: '* Cover *'})
      return marks
    },
    bookmarkCurrent: {
      get: function() {
        var pageNo = Math.min(this.previewPageNumber, this.previewPages.length-1)
        return this.previewPages[pageNo].pid
      },
      set: function(pid) {
        if (this.previewPages[this.previewPageNumber]
            && pid != this.previewPages[this.previewPageNumber].pid) {
          for (var i=0; i<this.previewPages.length; ++i) {
            if (this.previewPages[i].pid == pid &&
                this.previewPages[i].offset == 0) {
              console.log('was bookmark', pid, i, this.previewPageNumber)
              this.previewPageNumber = (i+1) & ~1
              console.log('set bookmark', pid, i, this.previewPageNumber)
              break
            }
          }
        }
      }
    },
    pageRightPid: function() {
      return this.bookmarkCurrent
    },
    pageLeftPid: function() {
        var pageNo = Math.max(this.previewPageNumber-1, 0)
        return this.previewPages[pageNo].pid
    },
    totalPages: function() {
      var totalPages = 0
      var uncounted = 0
      if (!this.selectedPostsIds || !this.project) {
        return 10;
      }
      this.selectedPostsIds.forEach( p => {
        if (this.pageCounts[p]!==undefined) totalPages += this.pageCounts[p]
        else if (this.pageEstimates[p]!==undefined) totalPages += this.pageEstimates[p]
        else uncounted++
      })
      // guesstimate the rest
      var n = this.selectedPostsIds.length
      if (n==uncounted) {
        totalPages = n*2
      } else if (uncounted) {
        totalPages *= n / (n - uncounted)
      }
      if (this.project.selections && this.project.selections.coverShowTitlePage) {
        totalPages += 2
      }
      return Math.floor(totalPages)
    },
    averagePostLength: function() {
      return (this.totalPages - 2) / this.selectedPostsIds.length
    },
    renderStatsReady : function() {
      return this.renderStatsAvailability >= 3
    },
    renderPending: function() {
      if (this.renderer) {
        return Array.concat(this.renderer.active, this.renderer.requested)
      }
      return []
    },
    coverName: function() {
      return 'cover' + (this.volid ? '-'+this.volid : '')
    },
    hasTableOfContents: function() {
      return this.project && this.project.selections
        && (this.project.selections.tweaks||[]).includes('table_of_contents')
    },
    tocName: function() {
      return 'toc' + (this.volid ? '-'+this.volid : '')
    },
    previewPages: function() {
      var posts = this.selectedPosts
      var pageCounts = this.pageCounts
      var pageEstimates = this.pageEstimates
      var previews = this.previews
      var pages = []
      pages.push(this.composePreviewPage(this.coverName, 'Front Cover', 0))
      pages.push({pid: '_blank', title: 'inner cover', offset: 0, page: 0})
      pages.push(this.composePreviewPage(this.coverName, 'Title Page', 2))
      pages.push(this.composePreviewPage(this.coverName, 'Copyright Page', 3))

      posts.forEach((p) => {
        if (!p) {
          console.log('!p transitionary state hopefully')
        } else if (pageCounts[p.pid]!==undefined) {
          for (var i=0; i<pageCounts[p.pid]; ++i) {
            pages.push(Object.assign(this.composePreviewPage(p.pid, p.title, i),
                                     { post: p }))
          }
        } else if (pageEstimates[p.pid]!==undefined) {
          for (var i=0; i<pageEstimates[p.pid]; ++i) {
            pages.push({pid: p.pid, title: p.title, post: p, page: 0, offset: -i})
          }
        } else {
          pages.push({pid: p.pid, title: p.title, post: p, page: 0, offset: -999})
        }

        //if (this.hasTableOfContents) {
        //  pages.push(this.composePreviewPage(this.tocName, 'Contents', 0))
        //  pages.push(this.composePreviewPage('_blank', 'more toc', 0))
        //}
      
        
      })
      if (pages.length %2 == 1) {
        pages.push({pid: '_blank', title: 'blank', page: 0, offset: 0})
      }
      pages.push({pid: '_blank', title: 'inner cover', page: 0, offset: 0})
      pages.push(this.composePreviewPage(this.coverName, 'Back Cover', 1))
      //console.log('previewPages:', pages)
      return pages
    },
    layoutsDone: function() {
      console.log('layoutsDone')
      if (this.selectedPosts && this.pageEstimates) {
        return this.selectedPosts.filter(p=>p && this.pageEstimates[p.pid] !== undefined)
      }
      return []
    },
    rendersDone: function() {
      if (this.selectedPosts && this.pageCounts) {
        return this.selectedPosts.filter(p=>p && this.pageCounts[p.pid] !== undefined)
      }
      return []
    },
    progressPercent: function() {
      return Math.floor((this.layoutsDone.length + this.rendersDone.length*3)*25/this.selectedPosts.length)
    },
    expectedPrice: function() {
      var price = 0
      var pages = this.totalPages
      while (pages > 0) {
        price += Pricer.calculatePrice(this.format,
                                       Math.min(pages,this.maxPages), this.$store.state.currency)
        pages -= this.maxPages
      }
      return price
    },
    volumesRequired: function() {
      var pagesWithoutTitle = this.totalPages - 2
      var maxPagesExcludingTitle = this.maxPages - 2
      return parseInt((pagesWithoutTitle+maxPagesExcludingTitle-1)/maxPagesExcludingTitle)
    },
    maxPages: function() {
      var p = this.projectLoaded ? this.project.maxPages : 0
      return p || this.$store.state.maxPages
    },
    tooManyPages: function() {
      return this.totalPages > this.maxPages
    },
    volumesPrintStatus: function() {
      if (this.isVolumated) {
        return Object.values(this.project.selections.volumes)
          .map( vol => this.pageCounts['cover-vol'+vol.volumeNumber]!==undefined
                && vol.selectedPosts
                && vol.selectedPosts.every(p => this.pageCounts[p]!==undefined) )
      }
      return []
    },
    allVolumesPrintable: function() {
      return this.volumesPrintStatus.every(p=>p)
    },
    canPrint: function() {
      var val = this.projectLoaded
          && this.renderStatsReady
          && this.rendersDone.length == this.selectedPosts.length
          && (this.showVolumesList
              ? this.allVolumesPrintable
              : !this.tooManyPages)
      //console.log("recalc canPrint=", val)
      return val
    },
    previewComplete: function() {
      var ready = this.renderStatsReady
      var val = (this.rendersDone.length == this.selectedPosts.length && ready)
      //console.log("recalc previewComplete", val)
      return val
    },
    devMode: function() {
      return this.$store.state.dev.developmentClient
    },
    photobookMode: function() {
      return this.project && this.project.selections && this.project.selections.style == 'photobook'
    },
    buttonClass: function() {
      var devButtons = this.project.isLocked && this.devMode
      return  'button ' + (devButtons ? 'dev-only' : 'is-primary')
    },
    errormsg: function() {
      if (this.taskErrorCount >= 40) {
        if (this.project.feed.cms == 'wixsite') {
          return `Unfortunately, due to recent changes in how Wix
                  deliver their content some Wix blogs no longer work with
                  PixxiBook. We are actively working to find a way to fix this but
                  we don't have a timeline yet. Sorry!<br><br>
                  If you would like to be notified when we have fixed
                  it, please contact us at
                  <a href="mailto:support@pixxibook.com">support@pixxibook.com</a>`
        }
        return `We're having trouble communicating with our
                workers... Please try reloading the page or come back
                again later. If the problem persists then please
                contact us and we'll get right on it.`
      }
      return undefined
    },
    renderTags: function() {
      var tags = [this.format, 'draft']
      if (this.projectLoaded && this.project.selections.styleHash) {
        tags.push(this.project.selections.styleHash)
      }
      return tags
    },
    renderTagsString: function() {
      return this.renderTags.join('_')
    },
    showLookInsideHint: function() {
      return !this.showVolumesList && !this.hasViewedInside && this.previewPageNumber==0
    },
  },
  methods: {
    updateCover: function() {
      console.log('updateCover')
      if (this.showVolumesList) {
        Object.keys(this.project.selections.volumes).forEach( volid => {
          this.rerenderRequests.unshift('cover-'+volid)
        })
      } else {
        this.rerenderRequests.unshift(this.coverName)
      }
    },
    updateToc: function() {
      console.log('updateToc')
      if (this.hasTableOfContents) {
        this.rerenderRequests.unshift(this.tocName)
      }
    },
    forceLayoutPost: function(pid) {
      if (pid != this.coverName && pid != this.tocName) {
        console.log('forceLayoutPost', pid)
        this.relayoutRequests.unshift(pid)
      }
    },
    forceRenderPost: function(pid) {
      console.log('forceRenderPost', pid)
      this.rerenderRequests.unshift(pid)
    },
    composePreviewPage: function(pid, title, pagenum) {
      var pageCount = this.pageCounts[pid]
      if (!pageCount || !this.previews[pid]) {
        return { pid: pid, title: title, page: pagenum, offset: -pagenum }
      }
      var sheet = Math.floor(pagenum / 10)
      var lastSheet = Math.floor(pageCount / 10)
      var isLastSheet = (sheet == lastSheet)
      var pagesThisSheet = isLastSheet ? (pageCount - lastSheet*10) : 10
      var offset = (pagenum % 10) / pagesThisSheet
      var result = { pid: pid, title: title,
                     pending: this.renderPending.indexOf(pid) >= 0,
                     img: this.previews[pid]['p'+(sheet*10)],
                     page: pagenum, offset: offset }
      return result
    },
    requestPreviewPost: function(pid) {
      if (!this.renderStatsReady) {
        return
      }
      if (this.pageCounts[pid]) {
        return
      }
      if (pid != this.coverName
          && pid != this.tocName
          && this.pageEstimates[pid]===undefined) {
        console.log('request layout of ',pid)
        this.layoutRequests.unshift(pid)
      } else {
        console.log('request render of ',pid)
        this.renderRequests.unshift(pid)
      }
    },
    previewPrev : function() {
      if (this.previewPageNumber > 1) { this.previewPageNumber-=2; }
    },
    previewNext : function() {
      if (this.previewPageNumber < this.previewPages.length-1) {
        this.previewPageNumber += 2
      }
    },
    previewPostPrev : function() {
      var pageNo = this.previewPageNumber
      if (pageNo > 1) {
        var pid = this.previewPages[pageNo-1].pid
        if (this.previewPages[pageNo-1].page != 0) {
          // find start of post already on left hand page
          pid = -1
        }
        do {
          pageNo -= 2;
        } while (pageNo > 1
                 && (this.previewPages[pageNo].pid==pid || this.previewPages[pageNo].page!=0)
                 && (this.previewPages[pageNo-1].pid==pid || this.previewPages[pageNo-1].page!=0))
        this.previewPageNumber = pageNo
      }
    },
    previewPostNext : function() {
      var pageNo = this.previewPageNumber
      if (pageNo < this.previewPages.length-2) {
        var pid = this.previewPages[pageNo].pid
        do {
          pageNo += 2;
        } while (pageNo < this.previewPages.length-2
                 && this.previewPages[pageNo].pid==pid
                 && this.previewPages[pageNo-1].pid==pid)
        this.previewPageNumber = pageNo
      }
    },
    publish: function() {
      var pages = this.previewPages.slice()
      var tag = this.projid + '-' + Math.floor(Math.random() * 1000000000)
      var book = { uid: this.uid,
                   projid: this.projid,
                   format: this.format,
                   pages: pages,
                   title: this.project.selections.coverTitle,
                   author: this.project.selections.coverAuthor,
                   site: this.project.site,
                   public: false,
                   timestamp: { '.sv': 'timestamp' } }
      console.log("publishing gallery ", book)
      var gallery = db.ref('/gallery').child(tag).set(book);
      this.published = tag
      
    },
    addToCart: function() {
      if (this.isVolumated) {
        Object.keys(this.project.selections.volumes).forEach( volid => {
          this.$store.dispatch('addToCart', { bookid: this.projid+'/'+volid, quantity: -1 })
          ActionLog('checkout', {bookid: this.projid+'/'+volid})
        })
      } else {
        this.$store.dispatch('addToCart', { bookid: this.bookid, quantity: -1 })
        ActionLog('checkout', {bookid: this.bookid})
      }
      this.$router.push('/checkout')
    },
    rebindRenderOutputs: function() {
      console.log('rebindRenderOutputs', this.renderTagsString)
      if (this.renderStatsAvailability) {
        this.$unbind("pageEstimates")
        this.$unbind("pageCounts")
        this.$unbind("previews")
        this.renderStatsAvailability = 0
      }
      
      var pcpath = ['/renders',this.uid,this.projid, this.renderTagsString].join('/')
      this.$bindAsObject("pageEstimates", db.ref(pcpath+'/stats/pageestimate'),
                         null, () => this.renderStatsAvailability++)
      //if (this.$firebaseRefs['pageCounts']) this.$unbind("pageCounts")
      this.$bindAsObject("pageCounts", db.ref(pcpath+'/stats/pagecounts'),
                         null, () => this.renderStatsAvailability++)
      //this.$unbind("previews")
      this.$bindAsObject("previews", db.ref(pcpath+'/preview'),
                         null, () => this.renderStatsAvailability++)
      console.log('rebindRenderOutputs Done', this.renderTagsString)
    },
    renewTaskers: function() {
      console.log('renewTaskers')
      this.taskSuccessCount = 0
      this.taskErrorCount = 0
      
      if (this.layouter) {
        this.layouter.$off()
        this.layouter.cancel()
      }
      if (this.renderer) {
        this.renderer.$off()
        this.renderer.cancel()
      }
      
      this.layouter = Tasker.createPostTasker('layouter', { project: this.projid,
                                                            render: this.renderTags })
      this.renderer = Tasker.createPostTasker('renderer', { project: this.projid,
                                                            render: this.renderTags,
                                                            widthpx: 840 })

      this.layouter.$on('success', () => { this.taskSuccessCount++ })
      this.renderer.$on('success', () => { this.taskSuccessCount++ })
      this.layouter.$on('error', this.onTaskError.bind(null,'layout'))
      this.renderer.$on('error', this.onTaskError.bind(null,'render'))
    },
    onTaskError: function(tasker, pids, errmsg, info) {
      this.taskErrorCount++
      
      while (this.taskErrors.length > 5) {
        this.taskErrors.shift()
      }

      if (this.taskErrors.length > 0
         && JSON.stringify(this.taskErrors[this.taskErrors.length-1].info) == JSON.stringify(info) ) {
        this.taskErrors[this.taskErrors.length-1].count++
        console.log('repeated error this.taskErrors[this.taskErrors.length-1]')
      } else {
        this.taskErrors.push({tasker, count: 1, pids, info})
      }
      
      if ((this.taskErrorCount%5) == 0) {
        ActionLog('error', {bookid: this.bookid,
                            count: this.taskErrorCount,
                            tasker: tasker,
                            pids: pids,
                            msg: errmsg,
                            info})
      }
    },
    idleTick: function() {
      if (this.layouter && !this.layouter.task) {
        //console.log('layouter idle')
        var postsNeedingLayout = this.selectedPostsIds.filter(p=>this.pageEstimates[p]===undefined)
        if (postsNeedingLayout.length) {
          //console.log('do layout=', postsNeedingLayout, this.pageEstimates)
          this.layouter.updateOutstanding(postsNeedingLayout)
        } else if (this.renderer && !this.renderer.task) {
          //console.log('renderer idle')
          var postsNeedingRender = this.selectedPostsIds.filter(p=>this.pageCounts[p]===undefined)
          if (postsNeedingRender.length) {
            //console.log('do render=', postsNeedingLayout, this.pageEstimates)
            this.renderer.updateOutstanding(postsNeedingRender)
          }
        }
      }
      this.idleTimeout = setTimeout(this.idleTick, 1000)
    },
  },
  watch: {
    renderStatsReady(newReady, old) {
      console.log('renderStatsReady', old,'->',newReady, this.renderStatsAvailability)
      if (!newReady) { return }
      if (!this.previews || !this.previews[this.coverName]) {
        this.requestPreviewPost(this.coverName)
      }
      if (this.selectedPostsIds) {
        var postsNeedingLayout = this.selectedPostsIds.filter(p=>this.pageEstimates[p]===undefined)
        //console.log('watch renderStatsReady layout=', postsNeedingLayout, this.pageEstimates)
        this.layouter.updateOutstanding(postsNeedingLayout)
      }
    },
    selectedPostsIds(newPostList, oldPostList) {
      if (!this.renderStatsReady) { return }
      var postsNeedingLayout = newPostList.filter(p=>this.pageEstimates[p]===undefined)
      //console.log('watch selectedPosts layout=', postsNeedingLayout, this.pageEstimates)
      this.layouter.updateOutstanding(postsNeedingLayout)
      this.updateToc()
    },
    layoutRequests(newReq, oldReq) {
      newReq = newReq.filter((p,i)=>newReq.indexOf(p)==i)
      //console.log('layoutRequests: ', newReq)
      if (newReq.length) {
        this.layouter.prioritise(newReq)
        this.layoutRequests = []
      }
    },
    renderRequests(newReq, oldReq) {
      newReq = newReq.filter((p,i)=>newReq.indexOf(p)==i)
      //console.log('renderRequests: ', newReq)
      if (newReq.length) {
        this.renderer.prioritise(newReq)
        this.renderRequests = []
      }
    },
    relayoutRequests(newReq, oldReq) {
      newReq = newReq.filter((p,i)=>newReq.indexOf(p)==i)
      //console.log('relayoutRequests: ', newReq)
      if (newReq.length) {
        this.layouter.force(newReq)
        this.relayoutRequests = []
      }
    },
    rerenderRequests(newReq, oldReq) {
      newReq = newReq.filter((p,i)=>newReq.indexOf(p)==i)
      //console.log('rerenderRequests: ', newReq)
      if (newReq.length) {
        this.renderer.force(newReq)
        this.rerenderRequests = []
      }
    },
    layoutsDone(newVal, oldVal) {
      if (!this.renderStatsReady) { return }
      console.log('layoutsDone',oldVal.length,'->',newVal.length)
      if (!this.previews || !this.previews[this.coverName]) {
        this.requestPreviewPost(this.coverName)
      }
      
      if (newVal.length != oldVal.length && oldVal.length != 0) {
        //as layouts progress, trigger render some posts
        var numToRender = (newVal.length - this.rendersDone.length)
        if (numToRender > 5 || newVal.length == this.selectedPostsIds.length) {
          numToRender = Math.min(numToRender, 3)
          var postsNeedingRender = this.selectedPostsIds.filter(p=>!this.pageCounts[p]).slice(0,numToRender)
          this.renderer.updateOutstanding(postsNeedingRender)
        }
        // once all posts are layouted, rebuild the mosaic cover
        if (newVal.length == this.selectedPostsIds.length
            && !this.project.selections.coverShowCustomImage
            && !this.initialLoad) {
          this.updateCover()
        }
        this.initialLoad = false
      }
    },
    previewPageNumber(pageNumber, prevPageNumber) {
      if (!this.project) {
        console.log("prevPageNumber: avoid too early render!")
        return
      }
      if (pageNumber > 0) {
        this.hasViewedInside = true
        //console.log('hasViewedInside', this.previewPageNumber, this.hasViewedInside)
      }

      var pages = [pageNumber, pageNumber-1]
      var direction = pageNumber < prevPageNumber ? -1 : 1
      for (var i=1; i<5; ++i) {
        pages.push(pageNumber + i*direction)
      }
      for (var i=0; i<pages.length; ++i) {
        var p = pages[i]
        if (p >= 0 && p < this.previewPages.length) {
          var pid = this.previewPages[p].pid
          if (pid != '_blank') {
            this.requestPreviewPost(pid)
          }
        }
      }
    },
    pageCounts(newCounts, oldCounts) {
      console.log("pageCounts update: ", newCounts, oldCounts);
    },
    format(newFormat) {
      //this.rebindRenderOutputs()
      //this.renewTaskers()
    },
    renderTagsString(v,o) {
      console.log('renderId changed:',v,o,this.renderTags)
      if (this.projectLoaded) {
        this.rebindRenderOutputs()
        this.renewTaskers()

        // make a cover and toc asap 
        this.$firebaseRefs.previews.once('value', snap => {
          var previews = snap.val()
          if (!previews || !previews[this.coverName]) {
            this.requestPreviewPost(this.coverName)
          }
        })
      }
    },
    previewComplete(newVal, oldVal) {
      console.log('previewComplete changed:', newVal, oldVal)
      if (!this.project || !this.project.site) return; 
      if (newVal == oldVal) return;
      if (newVal) {
        ActionLog('preview_ok', {bookid: this.bookid})
        this.$firebaseRefs.project.update({'selections/pageCount': this.totalPages})
      } else {
        this.$firebaseRefs.project.update({'selections/pageCount': false})
      }
    },
    tooManyPages(newVal, oldVal) {
      if (this.rendersDone.length == this.selectedPosts.length
          && newVal && !oldVal) {
        ActionLog('preview_toolong', {bookid: this.bookid})
      }
    },
  },
  beforeMount: function() {
    this.projid = this.$route.params.projid
    this.volid = this.$route.params.volid
    console.log('beforeMount')
    //this.rebindRenderOutputs()

    var proj = db.ref("/projects/"+this.uid+"/"+this.projid)
    this.$bindAsObject("project", proj)
    var posts = db.ref("/projects/"+this.uid+"/"+this.projid+"/posts")
    this.$bindAsObject("posts", posts)

    proj.once("value", snap => {
      var projinfo = snap.val()
      //console.log('got PROJINFO', projinfo)
      
      if (!projinfo || !projinfo.site) {
        console.log("error - non existant project")
        this.$router.push('/mybooks/')
        return
      }
      
      if (this.volid) {
        var volumes = (projinfo.selections || {}).volumes
        if (!volumes || !volumes[this.volid]) {
          console.log("error - non existant volume", this.volid)
          this.$router.push('/projects/'+this.projid)
          return
        }
      }

                             
      if (!projinfo.selections || !projinfo.selections.configuredCover)
      {
        console.log("initialise cover spec")
        var initial = { coverTitle: projinfo.title || 'Unknown Title',
                        coverDescription: projinfo.description || '',
                        coverShowTitlePage: true,
                        coverCustomImage: '6x6',
                      }
        //source-specific initial seclections        
        //TODO - should probably be set up elsewhere
        if (projinfo.feed && projinfo.feed.cms) {
          Object.assign(initial, { cms: projinfo.feed.cms })
        }
        if (projinfo.link && (projinfo.link.includes('gpquicky')
                             || projinfo.link.includes('photos.google'))) {
          Object.assign(initial, { format: 'a4r',
                                   singlePagePerPost: 1,
                                   style: 'photobook', })
        }
        //end TODO
        var sels = db.ref("/projects/"+this.uid+"/"+this.projid+"/selections")
        sels.update(initial)
      }
      if (!this.volid && (!projinfo.selections ||
                          !projinfo.selections.selectedPosts ||
                          !projinfo.selections.configuredPosts)) {
        this.showPostsConfigurator = true
      }

      ActionLog('preview', {'bookid': this.bookid})
    });
  },
  mounted: function() {
    console.log('mounted')
    //this.renewTaskers()
    this.idleTimeout = setTimeout(this.idleTick, 1000)
  },
  beforeRouteUpdate: function(to, from, next) {
    var volid = to.params.volid
    console.log('beforeRouteUpdate to',volid)
    if (volid
        && volid != this.volid
        && this.projectLoaded
        && (!this.project.selections.volumes
            || !this.project.selections.volumes[volid])) {
      next(false)
    } else {
      this.volid = to.params.volid
      this.previewPageNumber = 0
      this.initialLoad = true
      next()
    }
  },
  beforeDestroy: function() {
    console.log('beforeDestroy')
    if (this.idleTimeout) { clearTimeout(this.idleTimeout) }
    if (this.layouter) this.layouter.cancel()
    if (this.renderer) this.renderer.cancel()
    this.layouter = undefined
    this.renderer = undefined
  },
}
</script>



<style> /* fix bullets and wonkiness in buefy pagination */
ul.pagination-list li { list-style: none; margin-top: 0; }
ul.pagination-list { margin-top: 0; }
</style>

<style scoped>

#maincontent {
    padding-top: 0.5rem;
    display: flex;
    flex-wrap: wrap;
}
#previewbox {
    flex-grow: 1;
    max-width: 100%;
}
#infobox {
    flex-wrap: wrap;
    flex-justify: space-between;
    margin: 0px;
}
#infobox .tile h3 {
    x-font-size: 120%;
}
#infobox .message-body {
    padding: 0;
}
.keymapped {
    /* kill focus active border */
    outline: none;
}

#preview-area {
    
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f8f3e6;
    padding-top: 10px;
    position: relative;
}
#back-to-volumes-list {
}
#preview-holder-outer {
    max-width: 100%;
    position: relative;
}
#preview-holder {
    margin: 1em;
}
.format-a4 #preview-holder {
    max-width: 100vh;
}
.format-pa4 #preview-holder {
    max-width: 100vh;
}
.format-a4r #preview-holder {
    max-width: 200vh;
}
.format-sq #preview-holder {
    max-width: 150vh;
}
.preview-box {
    max-width: 100%;
    display: inline-block;
    position: relative;
}
.format-a4 .preview-box {
    width: 1688px;
}
.format-pa4 .preview-box {
    width: 844px;
}
.format-a4r .preview-box {
    width: 1188px;
}
.format-sq .preview-box {
    width: 1188px;
}

.page-container {
    width: 100%;
    padding: 1em;
}
.page-holder {
    margin: 0;
}
.page {
    width:100%;
    height: 100%;
    overflow: hidden;
}
.page-small.page-sizer {
    /*width: 400px; height: 564px;*/
    width: 200px; height: 282px;
}
.page .blank {
    width: 100%;
    height: 100%;
    color: #ccc;
    font-size: 80%;
    padding: 40% 2em 0 2em;
}
.page .blank:after {
    content: 'this page blank'
}

.page .loading {
     background: url(/static/loading_spinner.gif) no-repeat center center #fff;
}
.fit-parent {
     width: 100%;
     height: 100%;
}
.page .contents {
    background: url(/static/loading_spinner.gif) no-repeat center center #fff;
}
.page .contents img {
    width:100%;
}
.page .contents.pending img {
    opacity: 0.5;
}
.hbox {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    x-width: 90%;
    x-margin: auto;
    max-width: 50vw;
}
.slider {
    margin-left: 1rem;
    margin-right: 1rem;
    width: 14rem;
    max-width: 32rem;
}
.button {
    margin-bottom: 0.5rem;
}
.bookmarks {
    max-width: 20em;
    max-width: 80vw;
}


span.approx {
    font-style: italic;
    color: red;
}
.warning-banner {
    padding: 1ex;
    width: 100%;
    margin-bottom: 1ex;
}
.errormsg {
    position: absolute;
    top: 40%;
    min-width: 40%;
    box-shadow: 3px 3px 20px 12px rgba(0,0,0,.7);
}

.inline-button {
    vertical-align: middle;
}

.hint {
    color: white;
    background: rgba(1,1,1,0.8);
    border: 2px solid white;
    box-shadow: 2px 2px 2px 1px rgba(0,0,0,0.5);
    border-radius: 50% / 20%;
    padding: 1rem;
    margin-top: 2em;
    margin-left: auto;
    margin-right: 10%;
    position: absolute;

    min-height: 4rem;
    left: 66%;
    bottom: 50%;
    font-size: 150%;
    animation: zoomer 1s infinite; 
}

.fade-enter-active, .fade-leave-active {
    transition: opacity 500ms;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
}


.hint.clickable {
    cursor: pointer;
}

@keyframes zoomer {
   0%, 100% { transform: scale(1); } 
   50% { transform: scale(0.9) rotate(3deg); } 
}

.volumes-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
}

.volume-card {
    margin: 1em;
    width: 10vw !important;
    min-width: 13rem;
    max-width: 20rem;
}

.format-a4r .volume-card {
    width: 20vw !important;
    max-width: 35rem;
}
</style>



// WEBPACK FOOTER //
// src/components/ProjPage.vue